import {
  DEFAULT_MEETING_SETTINGS,
  EXPIRY_GRACE_MS,
  MEETING_ERROR_CODES,
  MEETING_SEATS_MAX_DEFAULT,
} from '../constants/meetings.constants'
import type {
  MeetingCredential,
  MeetingParticipant,
  MeetingRecord,
  MeetingSettings,
  MeetingStatus,
  MeetingType,
  RecurringConfig,
} from '../models/meeting.model'
import {
  MeetingError,
  MeetingValidationError,
  type ValidationErrors,
} from './meetings-error'
import {
  meetingAccessRepo,
  meetingCredentialsRepo,
  meetingsRepo,
} from './meetings.repo'
import {
  generatePassword,
  generateSalt,
  hashPassword,
  verifyPassword,
} from './password.crypto'
import {
  canonicalTimezone,
  entryForTimezone,
  formatTimezoneLabel,
  offsetMinutesAt,
  simulateLatency,
  utcMsToZonedParts,
} from '@/shared/utils'
import {
  generateMeetingId,
  resolveStartMs,
  validateDuration,
  validateMeetingIdFormat,
  validateParticipantName,
  validatePassword,
  validateRecurring,
  validateSeats,
  validateStartTime,
  validateTitle,
} from '../utils/meetings.validators'

/**
 * Meeting service — the only module any UI/state code should reach into.
 * Owns:
 *   - validation orchestration (delegates rules to utils/meetings.validators)
 *   - record creation, mutation, deletion (delegates persistence to meetings.repo)
 *   - credential management (delegates hashing to password.crypto)
 *   - join-time gating (existence, schedule window, lock, password)
 *
 * Surface kept verb-first (`createInstant`, `schedule`, `join`, `validateJoin`)
 * so it reads like a real REST client.
 */

// ── inputs ──────────────────────────────────────────────────────────────

export interface CreateInstantInput {
  title: string
  durationMinutes: number
  seats: number
  maxSeats?: number
  host: string
  hostEmail?: string
  hostId?: string
  settings?: Partial<MeetingSettings>
}

export interface ScheduleInput {
  title: string
  description?: string
  date: Date | undefined
  time: string // "HH:mm"
  durationMinutes: number
  timezone: string
  invitees?: string[]
  recurring?: boolean
  recurringConfig?: RecurringConfig
  password?: string // optional plaintext; if passwordEnabled and omitted we generate one
  settings: Partial<MeetingSettings>
  host: string
  hostEmail?: string
  hostId?: string
  seats?: number
}

export interface UpdateScheduleInput extends Omit<ScheduleInput, 'host'> {
  host?: string
}

export interface JoinInput {
  meetingId: string
  name: string
  password?: string
}

export interface JoinDeepLinkInput {
  meetingId: string
  name: string
}

// ── helpers ─────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#6366f1',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
]

const getColors = (count: number): string[] =>
  Array.from({ length: count }, (_, i) => AVATAR_COLORS[i % AVATAR_COLORS.length])

const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?'

const CLOCK_OPTS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
}

/**
 * A meeting's clock time, in the timezone of whoever is looking at it.
 *
 * Always derive this from the epoch ms, never from a pre-formatted string.
 * The API returns `start`/`end` already rendered, but that rendering happens
 * on the server, in the server's timezone — UTC in every deployed
 * environment. Screens that printed those strings showed an 11:05 IST
 * meeting as 5:35 AM: the right instant, named in the wrong zone.
 *
 * Locale and `hour12` are pinned rather than left to the viewer. Passing `[]`
 * takes the browser's locale, and only some of them are 12-hour: en-GB, de-DE
 * and fr-FR all render this as a bare "11:05" with no AM/PM, and en-IN gives a
 * lowercase "am". A meeting time with no meridiem is ambiguous to read, so it
 * is always spelled out.
 */
export const formatMeetingTime = (ms: number): string =>
  new Date(ms).toLocaleTimeString('en-US', CLOCK_OPTS)

/** Date and clock time together, for the one-line "when is this" summary. */
export const formatMeetingDateTime = (ms: number): string =>
  new Date(ms).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...CLOCK_OPTS,
  })

/**
 * The suffix to append after a meeting's viewer-local time when the
 * meeting's own zone reads a different clock at that instant, e.g.
 * " (4:00 PM London)". Empty when the meeting carries no zone or when the
 * two zones agree.
 *
 * Compared by live UTC offset at the instant in question, not by zone id, so
 * a meeting saved with the legacy "Asia/Calcutta" and a viewer in
 * "Asia/Kolkata" (the same zone under two names) never shows a needless
 * suffix, and two different zones that happen to share an offset right now
 * correctly show none either.
 *
 * UTC counts as no zone. It is what the server writes when it was never told
 * one: every instant meeting is inserted with `timezone: 'UTC'`, the schedule
 * route defaults to it, and so does the column. So a stored "UTC" says nothing
 * about where the meeting was arranged, and treating it as a choice put
 * "10:23 AM - 10:53 AM (04:53 AM UTC)" on an instant meeting made in India.
 */
export const meetingZoneTimeSuffix = (ms: number, meetingTimezone?: string): string => {
  if (!meetingTimezone) return ''
  const canonical = canonicalTimezone(meetingTimezone)
  if (canonical === 'UTC' || canonical === 'Etc/UTC') return ''
  const entry = entryForTimezone(canonical)
  if (!entry) return ''
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const at = new Date(ms)
  if (offsetMinutesAt(canonical, at) === offsetMinutesAt(viewerTz, at)) return ''
  const time = at.toLocaleTimeString('en-US', { ...CLOCK_OPTS, timeZone: canonical })
  return ` (${time} ${entry.city})`
}

/** A clock range plus the readable label of the zone it is printed in. */
export interface MeetingZoneTime {
  range: string
  zoneLabel: string
}

/** Weekday, day and month in a given zone, pinned to 'en-US' for the same
 * reason CLOCK_OPTS is: a locale-default order would read differently from
 * one machine to the next. Produces "Tue 6 Oct". */
const shortDateInZone = (ms: number, zone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).formatToParts(new Date(ms))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('weekday')} ${get('day')} ${get('month')}`
}

/**
 * The start-end clock range in `zone`, with a short date prefix when the
 * calendar day in `zone` differs from the day in `compareZone` at the start
 * instant (a meeting that crosses midnight between the two zones). With no
 * `compareZone`, there is nothing to compare against and no date is added.
 */
const formatRangeInZone = (
  startMs: number,
  endMs: number,
  zone: string,
  compareZone?: string,
): string => {
  const start = new Date(startMs).toLocaleTimeString('en-US', { ...CLOCK_OPTS, timeZone: zone })
  const end = new Date(endMs).toLocaleTimeString('en-US', { ...CLOCK_OPTS, timeZone: zone })
  const range = `${start} - ${end}`
  if (!compareZone) return range
  const here = utcMsToZonedParts(startMs, zone)
  const there = utcMsToZonedParts(startMs, compareZone)
  const sameDay = here.year === there.year && here.month === there.month && here.day === there.day
  return sameDay ? range : `${shortDateInZone(startMs, zone)}, ${range}`
}

/**
 * Meeting Details needs both the original schedule and what it means for
 * whoever is reading it, not the one-or-the-other `meetingZoneTimeSuffix`
 * gives the compact list row.
 *
 * `meeting` is the range read in the meeting's own zone, always. `viewer` is
 * the same range read in the viewer's zone, included only when the two zones
 * disagree at the meeting's start (the same live-offset comparison
 * `meetingZoneTimeSuffix` uses, so the two helpers never disagree about
 * "different enough to show"). With no usable meeting zone (missing, or one
 * Intl does not recognise), `meeting` is null and `viewer` falls back to the
 * plain local reading, so an older meeting saved without a zone still shows
 * something.
 */
export function meetingTimesInZones(
  startMs: number,
  endMs: number,
  meetingTimezone?: string,
  viewerTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): { meeting: MeetingZoneTime | null; viewer: MeetingZoneTime | null } {
  const canonicalMeeting = meetingTimezone ? canonicalTimezone(meetingTimezone) : undefined
  const meetingEntry = canonicalMeeting ? entryForTimezone(canonicalMeeting) : undefined

  if (!canonicalMeeting || !meetingEntry) {
    return {
      meeting: null,
      viewer: {
        range: formatRangeInZone(startMs, endMs, viewerTimezone),
        zoneLabel: formatTimezoneLabel(viewerTimezone),
      },
    }
  }

  const meeting: MeetingZoneTime = {
    range: formatRangeInZone(startMs, endMs, canonicalMeeting, viewerTimezone),
    zoneLabel: formatTimezoneLabel(canonicalMeeting),
  }

  const at = new Date(startMs)
  const sameOffset = offsetMinutesAt(canonicalMeeting, at) === offsetMinutesAt(viewerTimezone, at)
  if (sameOffset) return { meeting, viewer: null }

  const viewer: MeetingZoneTime = {
    range: formatRangeInZone(startMs, endMs, viewerTimezone, canonicalMeeting),
    zoneLabel: formatTimezoneLabel(viewerTimezone),
  }
  return { meeting, viewer }
}

/** One labelled reading of a meeting's start: date, clock time and zone name. */
export interface MeetingEventTime {
  date: string
  time: string
  zoneLabel: string
}

const eventTimeInZone = (ms: number, zone: string): MeetingEventTime => ({
  // en-GB is pinned for dd/MM/yyyy, same reasoning as CLOCK_OPTS.
  date: new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(ms)),
  // The old product's wording, which these rows copy: "2:50 PM" (no leading
  // zero) and the zone's own id ("Asia/Calcutta"), not the readable label.
  time: new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone }),
  zoneLabel: zone,
})

/**
 * Meeting Details shows two labelled readings of the start: `yours` in the
 * viewer's zone and `original` in the zone the meeting was stored with. The
 * original is always present when the meeting has a usable zone, even when it
 * equals the viewer's, so the two rows never appear and disappear depending
 * on where the viewer sits. A missing or unrecognised zone gives `original:
 * null`, and so does UTC, for the reason `meetingZoneTimeSuffix` gives: it is
 * what the server stores when it was never told a zone, so every instant
 * meeting would otherwise claim to have been set in UTC. `endMs` is accepted
 * so callers pass the whole window, but only the start is read: the duration
 * is shown separately.
 */
export function meetingEventTimes(
  startMs: number,
  _endMs: number,
  meetingTimezone?: string,
  viewerTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): { yours: MeetingEventTime; original: MeetingEventTime | null } {
  const canonicalMeeting = meetingTimezone ? canonicalTimezone(meetingTimezone) : undefined
  const meetingEntry =
    canonicalMeeting && canonicalMeeting !== 'UTC' && canonicalMeeting !== 'Etc/UTC'
      ? entryForTimezone(canonicalMeeting)
      : undefined
  return {
    yours: eventTimeInZone(startMs, viewerTimezone),
    original:
      canonicalMeeting && meetingEntry ? eventTimeInZone(startMs, canonicalMeeting) : null,
  }
}

/** "15/10/2026, 2:50 PM, 30 minutes, (GMT+05:30) Kolkata" */
export const formatEventTime = (
  e: MeetingEventTime,
  minutes: number,
  minutesWord: string,
): string => `${e.date}, ${e.time}, ${minutes} ${minutesWord}, ${e.zoneLabel}`

const formatHM = formatMeetingTime

export function resolveStatus(startMs: number, endMs: number): MeetingStatus {
  const now = Date.now()
  // Tested end-first, and on `>=`. A meeting that ends exactly now has
  // ended. The old order asked "is now inside the window?" first with an
  // inclusive `<= endMs`, so at the instant the end time equalled now the
  // meeting still read 'ongoing' — which is exactly the instant Mark as Past
  // creates, because it sets the end time to now. The meeting stayed put and
  // only fell into Past on some later refresh, making the button look broken.
  if (now >= endMs + EXPIRY_GRACE_MS) return 'expired'
  if (now >= endMs) return 'completed'
  if (now >= startMs) return 'ongoing'
  return 'upcoming'
}

/**
 * Is this meeting over?
 *
 * The one place that answers it, because "past" decides whether Delete is
 * offered and the two screens had drifted into asking it differently — the
 * details page by end time alone, the dashboard not at all, so a finished
 * meeting still showed a Delete button there.
 *
 * Deleting is a CANCELLATION: it marks the meeting cancelled so it stops
 * appearing and nobody turns up to it. That is meaningless once the meeting has
 * happened, and the server agrees — `softDelete` refuses anything already ended
 * or cancelled and answers 404, so the button could only ever report that a
 * meeting the user was looking at "could not be found".
 *
 * Status AND end time, because both routes into the past have to be covered:
 * the clock passing the end, and Mark as Past, which brings the end time back
 * to now. Status is derived from those times, but a view rendered a moment
 * before the boundary still carries the older value.
 */
export function isPastMeeting(meeting: {
  status?: MeetingStatus
  endTimeMs?: number
}): boolean {
  if (meeting.status === 'completed' || meeting.status === 'expired') return true
  return typeof meeting.endTimeMs === 'number' && Date.now() > meeting.endTimeMs
}

const mergeSettings = (
  overrides: Partial<MeetingSettings> | undefined,
): MeetingSettings => ({
  ...DEFAULT_MEETING_SETTINGS,
  ...(overrides ?? {}),
})

const throwIfErrors = (errors: ValidationErrors): void => {
  if (Object.keys(errors).length > 0) {
    throw new MeetingValidationError(errors)
  }
}

const buildRecord = (
  args: {
    id: string
    type: MeetingType
    title: string
    description?: string
    host: string
    hostEmail?: string
    hostId?: string
    startTimeMs: number
    endTimeMs: number
    durationMinutes: number
    timezone: string
    invitees: string[]
    settings: MeetingSettings
    recurring: boolean
    recurringConfig?: RecurringConfig
    seats: number
  },
): MeetingRecord => {
  const {
    id,
    type,
    title,
    description,
    host,
    hostEmail,
    hostId,
    startTimeMs,
    endTimeMs,
    durationMinutes,
    timezone,
    invitees,
    settings,
    recurring,
    recurringConfig,
    seats,
  } = args
  const now = Date.now()
  return {
    id,
    meetingId: id,
    title: title.trim(),
    description: description?.trim() || undefined,
    host,
    hostEmail,
    hostId,
    type,
    startTimeMs,
    endTimeMs,
    durationMinutes,
    timezone,
    status: resolveStatus(startTimeMs, endTimeMs),
    start: formatHM(startTimeMs),
    end: formatHM(endTimeMs),
    duration: durationMinutes,
    attendees: [initials(host)],
    colors: getColors(1),
    seats,
    invitees: invitees.slice(),
    participants: [
      {
        id: hostId ?? `host_${id}`,
        name: host,
        email: hostEmail,
        role: 'host',
      },
    ],
    settings,
    recurring,
    recurringConfig: recurring ? recurringConfig : undefined,
    attachments: [],
    createdAt: now,
    updatedAt: now,
  }
}

const setPassword = async (
  meetingId: string,
  plaintext: string,
): Promise<MeetingCredential> => {
  const salt = generateSalt()
  const passwordHash = await hashPassword(plaintext, salt)
  const cred: MeetingCredential = {
    meetingId,
    passwordHash,
    salt,
    plaintext, // mock only — see model.ts comment
    updatedAt: Date.now(),
  }
  meetingCredentialsRepo.put(cred)
  return cred
}

// ── public API ──────────────────────────────────────────────────────────

export const meetingsService = {
  /** List all meetings (status refreshed against the wall clock). */
  list(): MeetingRecord[] {
    return meetingsRepo.all().map((m) => ({
      ...m,
      status: resolveStatus(m.startTimeMs, m.endTimeMs),
    }))
  },

  getById(id: string): MeetingRecord | undefined {
    const record = meetingsRepo.findById(id)
    if (!record) return undefined
    return { ...record, status: resolveStatus(record.startTimeMs, record.endTimeMs) }
  },

  refreshStatuses(): MeetingRecord[] {
    const updated = meetingsRepo.all().map((m) => ({
      ...m,
      status: resolveStatus(m.startTimeMs, m.endTimeMs),
    }))
    meetingsRepo.bulkReplace(updated)
    return updated
  },

  // ── Instant ────────────────────────────────────────────────────────────

  async createInstant(input: CreateInstantInput): Promise<MeetingRecord> {
    const errors: ValidationErrors = {}
    validateTitle(input.title, errors)
    validateSeats(input.seats, errors, input.maxSeats ?? MEETING_SEATS_MAX_DEFAULT)
    validateDuration(input.durationMinutes, errors)
    throwIfErrors(errors)

    // Mock backend round-trip — keeps the UI's loading states visible.
    await simulateLatency()

    const id = generateMeetingId()
    const startTimeMs = Date.now()
    const endTimeMs = startTimeMs + input.durationMinutes * 60_000
    const settings = mergeSettings(input.settings)

    const record = buildRecord({
      id,
      type: 'instant',
      title: input.title,
      host: input.host,
      hostEmail: input.hostEmail,
      hostId: input.hostId,
      startTimeMs,
      endTimeMs,
      durationMinutes: input.durationMinutes,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      invitees: [],
      settings,
      recurring: false,
      seats: input.seats,
    })

    meetingsRepo.insert(record)

    if (settings.passwordEnabled) {
      await setPassword(id, generatePassword())
    } else {
      // No password → grant access in this tab so the host doesn't need to
      // re-prove anything when navigating into the meeting.
      meetingAccessRepo.grant(id)
    }
    return record
  },

  // ── Scheduled ──────────────────────────────────────────────────────────

  async schedule(input: ScheduleInput): Promise<MeetingRecord> {
    const errors: ValidationErrors = {}
    validateTitle(input.title, errors)
    validateStartTime({ date: input.date, time: input.time, timeZone: input.timezone }, errors)
    validateDuration(input.durationMinutes, errors)
    validateRecurring(input.recurringConfig, !!input.recurring, errors, input.date)
    if (input.settings.passwordEnabled && input.password !== undefined) {
      validatePassword(input.password, errors)
    }
    throwIfErrors(errors)

    await simulateLatency()

    const startTimeMs = resolveStartMs({ date: input.date, time: input.time, timeZone: input.timezone })!
    const endTimeMs = startTimeMs + input.durationMinutes * 60_000
    const settings = mergeSettings(input.settings)
    const id = generateMeetingId()

    const record = buildRecord({
      id,
      type: 'scheduled',
      title: input.title,
      description: input.description,
      host: input.host,
      hostEmail: input.hostEmail,
      hostId: input.hostId,
      startTimeMs,
      endTimeMs,
      durationMinutes: input.durationMinutes,
      timezone: input.timezone,
      invitees: input.invitees ?? [],
      settings,
      recurring: !!input.recurring,
      recurringConfig: input.recurringConfig,
      seats: input.seats ?? 2,
    })

    meetingsRepo.insert(record)

    if (settings.passwordEnabled) {
      const plaintext = (input.password ?? '').trim() || generatePassword()
      await setPassword(id, plaintext)
    }

    return record
  },

  async updateSchedule(
    id: string,
    input: UpdateScheduleInput,
  ): Promise<MeetingRecord> {
    const existing = meetingsRepo.findById(id)
    if (!existing) {
      throw new MeetingError(MEETING_ERROR_CODES.MEETING_NOT_FOUND)
    }

    const errors: ValidationErrors = {}
    validateTitle(input.title, errors)
    validateStartTime({ date: input.date, time: input.time, timeZone: input.timezone }, errors)
    validateDuration(input.durationMinutes, errors)
    validateRecurring(input.recurringConfig, !!input.recurring, errors, input.date)
    throwIfErrors(errors)

    await simulateLatency()

    const startTimeMs = resolveStartMs({ date: input.date, time: input.time, timeZone: input.timezone })!
    const endTimeMs = startTimeMs + input.durationMinutes * 60_000
    const settings = mergeSettings(input.settings)

    const updated = meetingsRepo.update(existing.id, {
      title: input.title.trim(),
      description: input.description?.trim() || undefined,
      startTimeMs,
      endTimeMs,
      durationMinutes: input.durationMinutes,
      duration: input.durationMinutes,
      timezone: input.timezone,
      start: formatHM(startTimeMs),
      end: formatHM(endTimeMs),
      invitees: input.invitees ?? existing.invitees,
      settings,
      recurring: !!input.recurring,
      recurringConfig: input.recurring ? input.recurringConfig : undefined,
      status: resolveStatus(startTimeMs, endTimeMs),
    })!

    // Password toggle handling.
    if (settings.passwordEnabled) {
      if (input.password !== undefined && input.password.trim().length > 0) {
        await setPassword(existing.id, input.password.trim())
      } else if (!meetingCredentialsRepo.get(existing.id)) {
        await setPassword(existing.id, generatePassword())
      }
    } else {
      meetingCredentialsRepo.remove(existing.id)
    }

    return updated
  },

  // ── Join validation ────────────────────────────────────────────────────

  /**
   * Validates that a (name, meetingId) pair could plausibly join a meeting
   * without actually joining. Returns the record on success; throws either
   * a `MeetingValidationError` (form-level) or `MeetingError` (auth-level).
   */
  async validateJoin(input: JoinInput): Promise<MeetingRecord> {
    const errors: ValidationErrors = {}
    validateParticipantName(input.name, errors)
    validateMeetingIdFormat(input.meetingId, errors)
    throwIfErrors(errors)

    await simulateLatency()

    const record = meetingsRepo.findById(input.meetingId)
    if (!record) throw new MeetingError(MEETING_ERROR_CODES.MEETING_NOT_FOUND)

    const status = resolveStatus(record.startTimeMs, record.endTimeMs)

    if (status === 'cancelled') {
      throw new MeetingError(MEETING_ERROR_CODES.MEETING_CANCELLED)
    }
    if (status === 'expired') {
      throw new MeetingError(MEETING_ERROR_CODES.MEETING_EXPIRED)
    }
    if (status === 'completed') {
      // Once the meeting has wound down past its end time, no one can join
      // again. Surfaces as a distinct "completed" error so the UI can show
      // a precise message rather than a generic "expired" one.
      throw new MeetingError(MEETING_ERROR_CODES.MEETING_COMPLETED)
    }

    // Upcoming meetings are always joinable (early entry is fine — host
    // would otherwise have to babysit the lobby). The schedule is metadata,
    // not a hard gate. If we later need a strict window, re-introduce it
    // gated on a per-meeting setting.

    // Password handling.
    if (record.settings.passwordEnabled) {
      const credential = meetingCredentialsRepo.get(record.id)
      if (!credential) {
        // Password was enabled but no credential persisted — treat as open
        // (defensive: shouldn't happen via service-created meetings).
      } else if (input.password === undefined || input.password === '') {
        throw new MeetingError(MEETING_ERROR_CODES.PASSWORD_REQUIRED)
      } else {
        const ok = await verifyPassword(
          input.password,
          credential.salt,
          credential.passwordHash,
        )
        if (!ok) throw new MeetingError(MEETING_ERROR_CODES.INVALID_PASSWORD)
      }
    }

    return { ...record, status }
  },

  /**
   * Full join flow: validates, records the participant on the meeting,
   * registers the access grant in sessionStorage.
   */
  async join(input: JoinInput): Promise<MeetingRecord> {
    const record = await this.validateJoin(input)
    const participant: MeetingParticipant = {
      id: `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      name: input.name.trim(),
      role: 'participant',
      joinedAt: Date.now(),
    }
    const updated = meetingsRepo.update(record.id, {
      participants: [
        ...record.participants.filter((p) => p.name !== participant.name),
        participant,
      ],
      attendees: dedupe([...record.attendees, initials(participant.name)]),
      colors: getColors(record.attendees.length + 1),
    })
    meetingAccessRepo.grant(record.id)
    return updated ?? record
  },

  // ── Credentials (host-only access) ─────────────────────────────────────

  /** Returns the plaintext password — only available to the host in the mock
   * layer. In production this would be a host-authenticated server call. */
  getPasswordForHost(meetingId: string, hostId: string | undefined): string | undefined {
    const record = meetingsRepo.findById(meetingId)
    if (!record) return undefined
    if (hostId && record.hostId && record.hostId !== hostId) return undefined
    if (!hostId && record.hostEmail) return undefined
    const cred = meetingCredentialsRepo.get(record.id)
    return cred?.plaintext
  },

  hasGrantedAccess(meetingId: string): boolean {
    return meetingAccessRepo.hasGrant(meetingId)
  },

  grantAccess(meetingId: string): void {
    meetingAccessRepo.grant(meetingId)
  },

  // ── Mutations ──────────────────────────────────────────────────────────

  cancel(id: string): MeetingRecord | null {
    return meetingsRepo.update(id, { status: 'cancelled' })
  },

  remove(id: string): boolean {
    return meetingsRepo.remove(id)
  },

  markParticipantLeft(id: string, participantId: string): MeetingRecord | null {
    const record = meetingsRepo.findById(id)
    if (!record) return null
    const next = record.participants.map((p) =>
      p.id === participantId ? { ...p, leftAt: Date.now() } : p,
    )
    return meetingsRepo.update(record.id, { participants: next })
  },
}

function dedupe<T>(items: T[]): T[] {
  return Array.from(new Set(items))
}

// Re-export helpers consumers like the store will keep using.
export { getColors }
